// A spec's files: the usual table (DataTable: views, columns, Copy / CSV), one row per file slot (Gerbers, STEP…) with
// its kind, "In next release", its current upload and what you can do to it right there (view, download, new version,
// rename, delete); a file with several versions opens (its arrow, or a click) to a row per version, its history.
// Dropping files, folders or zips on the list (or choosing them) never uploads straight away: a drop plan at the top
// sorts them first (shared/spec-drop.ts: Gerber layers and drill files zipped into one file, BOM and placement files
// a slot each, a new export a new version of the slot it matches) and Upload sends them. One file dropped on a row is a
// new version of that row (with an optional note, kept with the version); several, or a folder, open the plan aimed
// at that row. Uploads stream with a progress bar and carry on if you switch tabs; uploads to one slot go one after
// another, so the file dropped last is the one that ends up current.
// KiCad projects --- with KiCad on the server (me().kicad), a dropped KiCad project is one upload ("<board> KiCad
// project.zip") that KiCad then makes the Gerbers, drill, placement, BOM and schematic from, into this spec, with its
// Board section; the plan leaves out older exports dropped with it. A toast says what KiCad made, or why it couldn't.
import { AlertTriangle, Check, Download, Eye, FileArchive, File as FileIcon, FileUp, LoaderCircle, Pencil, Trash2, Upload as UploadIcon } from "lucide-react";
import { type DragEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { FileVersion, Spec, SpecFile } from "../../../../shared/db.gen.ts";
import {
  type DropGroup,
  fileNeedStates,
  filesHint,
  kindsWithUploads,
  type NeedsItem,
  needsLabel,
  planDrop,
  skippedFiles,
} from "../../../../shared/spec-drop.ts";
import { fileSlotName, SPEC_FILE_KIND_LABELS, SPEC_FILE_KINDS, type SpecFileKind } from "../../../../shared/specs.ts";
import { ago } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { me } from "../../lib/session.ts";
import { useRow } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Select } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../ui/DataTable.tsx";
import { DropFace, hasFiles } from "../../ui/FileDrop.tsx";
import { toast } from "../../ui/toast.tsx";
import { type Dropped, droppedFiles, forPlan, hasFolder, makeUploads, peekZips, pickedFiles } from "./drop.ts";
import { bytes, day, downloadUrl, type SpecIndex, type Upload, type UploadResult, uploadVersion, useUploads, viewable } from "./lib.ts";

const KIND_OPTIONS = SPEC_FILE_KINDS.map((k) => ({ value: k, label: SPEC_FILE_KIND_LABELS[k] }));
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Each slot's uploads, in the order they were started: the server makes whichever finishes last the current one. */
const sending = new Map<string, Promise<boolean>>();

/** KiCad projects --- what KiCad made from `board`'s project, or why it made nothing. */
function reportKicad(board: string, file: File, r: UploadResult) {
  if (r.kicadError) return toast.error(`${board}'s project is kept, but KiCad made nothing: ${r.kicadError}`);
  if (!r.made) return toast.info(`${file.name} is the same as the current version, so KiCad made nothing new`);
  const fresh = r.made.files.filter((x) => !x.unchanged);
  toast.ok(
    fresh.length
      ? `${r.made.kicad} made ${board}'s ${fresh.map((x) => x.filename).join(", ")} and its Board section`
      : `${r.made.kicad} made the same files as before for ${board}`,
  );
  for (const w of r.made.warnings) toast.info(`${board}: ${w}`);
}

/**
 * Uploads `file` as the slot's next version, after any upload to it still going; false if it didn't go. KiCad projects
 * --- `kicad`: the board whose KiCad project it is, which KiCad then makes the files from.
 */
function send(fileId: string, file: File, note = "", kicad?: string): Promise<boolean> {
  const next = (sending.get(fileId) ?? Promise.resolve(true)).then(async () => {
    try {
      const r = await uploadVersion(fileId, file, note, !!kicad);
      if (kicad) reportKicad(kicad, file, r);
      else if (r.unchanged) toast.info(`${file.name} is the same as the current version; nothing changed`);
      else toast.ok(`Uploaded ${file.name}`);
      return true;
    } catch (e) {
      toast.error(`${file.name} didn't upload: ${(e as Error).message}`);
      return false;
    }
  });
  sending.set(fileId, next);
  next.then(() => sending.get(fileId) === next && sending.delete(fileId));
  return next;
}

/**
 * Files dropped or chosen, waiting in the drop plan of one spec; `target`: the row they were dropped on. A new `id`
 * starts a new plan (its choices start over).
 */
type Drop = { id: number; specId: string; files: Dropped[]; target?: string };
let drops = 0;

export function SpecFiles({ spec, ix, wide }: { spec: Spec; ix: SpecIndex; wide?: boolean }) {
  const files = ix.filesBySpec.get(spec.id) ?? [];
  const item = useRow("item", spec.item_id ?? ix.itemOfSpec.get(spec.id)); // a product's: its first variant
  const editable = can("spec_file.update");
  const uploads = useUploads();
  const [over, setOver] = useState(false);
  const [dropped, setDrop] = useState<Drop | null>(null);
  // A plan belongs to the spec it was dropped on (the panel can move to another item meanwhile).
  const drop = dropped?.specId === spec.id ? dropped : null;
  const [reading, setReading] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const folderPicker = useRef<HTMLInputElement>(null);
  const hint = item ? filesHint(item) : "";
  // A file dropped on a file's row is its new version: the row a drag is over (its rows carry data-file), and the
  // dropped file waiting for its note.
  const [overFile, setOverFile] = useState<string | null>(null);
  const [pending, setPending] = useState<{ fileId: string; file: File } | null>(null);
  const [note, setNote] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const versionPicker = useRef<HTMLInputElement>(null);
  const pickingFor = useRef<string | null>(null); // the file whose Upload a new version was clicked
  const fileById = new Map(files.map((f) => [f.id, f]));
  const pendingFile = pending ? fileById.get(pending.fileId) : undefined;

  // A new version of `file`: the first upload goes straight away; a new version asks for an optional note first.
  const choose = (file: SpecFile, f: File | undefined) => {
    if (!f) return;
    if (!(file.current_version_id && ix.versionById.get(file.current_version_id))) {
      send(file.id, f);
      return;
    }
    setNote("");
    setPending({ fileId: file.id, file: f });
  };

  // Folders are walked and zips looked into before the plan shows; a second drop adds to the open plan (a file
  // dropped again replaces the one before: planDrop).
  const add = async (read: Promise<Dropped[]>, target?: string) => {
    setReading(true);
    try {
      const list = await peekZips(await read);
      const specId = spec.id;
      if (list.length)
        setDrop((d) =>
          d?.specId === specId ? { ...d, files: [...d.files, ...list], target: target ?? d.target } : { id: ++drops, specId, files: list, target },
        );
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setReading(false);
    }
  };
  // The plan closes once uploaded (`sent`: how many of its files went), unless more were dropped while it was being
  // prepared: those stay, in a plan of their own.
  const close = (id: number, sent?: number) =>
    setDrop((d) => (d?.id !== id ? d : sent !== undefined && d.files.length > sent ? { id: ++drops, specId: d.specId, files: d.files.slice(sent) } : null));

  return (
    <div
      className={cx("relative flex flex-col gap-1 rounded-md", over && "outline-2 outline-accent outline-dashed")}
      onDragOver={(e) => {
        if (!editable || !hasFiles(e)) return;
        e.preventDefault();
        const on = fileAt(e.target);
        setOverFile(on);
        setOver(!on);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setOver(false);
        setOverFile(null);
      }}
      onDrop={(e) => {
        setOver(false);
        setOverFile(null);
        if (!editable || !e.dataTransfer.files.length) return;
        e.preventDefault();
        const file = fileById.get(fileAt(e.target) ?? "");
        if (!file) add(droppedFiles(e.dataTransfer));
        // One file dropped on a file's row is its new version; several, or a folder, open the plan aimed at it.
        else if (e.dataTransfer.files.length === 1 && !hasFolder(e.dataTransfer)) choose(file, e.dataTransfer.files[0]);
        else add(droppedFiles(e.dataTransfer), file.id);
      }}
    >
      {reading && !drop && (
        <div className="flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2 text-muted">
          <LoaderCircle className="size-3.5 animate-spin" /> Looking at the files…
        </div>
      )}
      {drop && (
        <DropPlan
          key={drop.id}
          spec={spec}
          files={files}
          ix={ix}
          drop={drop}
          title={item?.name ?? ""}
          reading={reading}
          uploading={uploads}
          onClose={(sent) => close(drop.id, sent)}
        />
      )}
      {pending && pendingFile && (
        <form
          className="flex items-center gap-2 rounded-md border border-accent bg-surface px-3 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            send(pendingFile.id, pending.file, note.trim());
            setPending(null);
          }}
        >
          <span className="max-w-72 shrink-0 truncate text-[12px] text-muted" title={pending.file.name}>
            {pending.file.name} ({bytes(pending.file.size)}) → a new version of {pendingFile.name}
          </span>
          <Input
            autoFocus
            value={note}
            placeholder="What changed? (optional, kept with this version)"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setPending(null)}
            className="h-7"
          />
          <Button size="sm" variant="primary" type="submit" kbd="↵">
            Upload
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
            Cancel
          </Button>
        </form>
      )}
      {files.length > 0 && (
        <>
          <div className="flex items-start gap-2">{item && <Needs item={item} files={files} />}</div>
          <FileTable
            files={files}
            ix={ix}
            editable={editable}
            uploads={uploads}
            wide={wide}
            overFile={overFile}
            renaming={renaming}
            onRename={setRenaming}
            onUpload={(f) => {
              pickingFor.current = f.id;
              versionPicker.current?.click();
            }}
          />
        </>
      )}
      {editable ? (
        // The shared file box's look (ui/FileDrop.tsx); the drop itself is this list's (onto a file's row, folders)
        <DropFace
          className="mt-1"
          over={over}
          onClick={() => picker.current?.click()}
          title={files.length ? "Add files or a folder, or drop one onto a file for a new version" : hint || "Gerbers, STEP, drawings, firmware…"}
          extra={
            <button type="button" className="text-muted hover:text-accent-text hover:underline" onClick={() => folderPicker.current?.click()}>
              Choose a folder
            </button>
          }
        />
      ) : (
        !files.length && <div className="px-2 py-2 text-muted">No files.</div>
      )}
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          add(Promise.resolve(pickedFiles(e.target.files)));
          e.target.value = "";
        }}
      />
      <input
        ref={versionPicker}
        type="file"
        hidden
        onChange={(e) => {
          const f = fileById.get(pickingFor.current ?? "");
          if (f) choose(f, e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={(el) => {
          // A folder picker: React has no prop for it.
          el?.setAttribute("webkitdirectory", "");
          folderPicker.current = el;
        }}
        type="file"
        hidden
        onChange={(e) => {
          add(Promise.resolve(pickedFiles(e.target.files)));
          e.target.value = "";
        }}
      />
    </div>
  );
}

/** The file whose row is under the pointer: each row carries `data-file`, found from anywhere in the row (its padding too). */
function fileAt(target: EventTarget | null): string | null {
  const el = target instanceof Element ? target : null;
  const own = el?.closest("[data-file]") ?? el?.closest("[data-table] [style*='grid-template-columns']")?.querySelector("[data-file]");
  return own?.getAttribute("data-file") ?? null;
}

/** One row per version, under its file (a file with one version, or none yet, is a row of its own). */
type FileLine = { id: string; file: SpecFile; version: FileVersion | null; order: number; versions: number; n: number };

/**
 * The files as the usual table: a file's row (or, with several versions, the header over them) has its name, the
 * revisions its current upload is in, the upload's name, size, date and note, its kind (click to change), In next
 * release and its actions; each version under it the same for that upload. A drag over a row lights its name.
 */
function FileTable({
  files,
  ix,
  editable,
  uploads,
  wide,
  overFile,
  renaming,
  onRename,
  onUpload,
}: {
  files: SpecFile[];
  ix: SpecIndex;
  editable: boolean;
  /** Uploads under way, by slot. */
  uploads: ReadonlyMap<string, Upload>;
  wide?: boolean;
  overFile: string | null;
  renaming: string | null;
  onRename: (fileId: string | null) => void;
  onUpload: (file: SpecFile) => void;
}) {
  const rows = useMemo<FileLine[]>(
    () =>
      files.flatMap((f, fi): FileLine[] => {
        const vs = ix.versionsByFile.get(f.id) ?? [];
        if (!vs.length) return [{ id: `f:${f.id}`, file: f, version: null, order: fi * 10000, versions: 0, n: 0 }];
        return vs.map((v, vi) => ({ id: `v:${v.id}`, file: f, version: v, order: fi * 10000 + vi, versions: vs.length, n: vs.length - vi }));
      }),
    [files, ix],
  );
  const currentOf = (f: SpecFile) => (f.current_version_id ? ix.versionById.get(f.current_version_id) : undefined);
  const fileOf = (group: FileLine[]) => group[0].file;
  // A file's cells (its row, or the header over its versions) use its current upload; a version's, its own.
  const shownVersion = (l: FileLine, nested: boolean) => (nested ? (l.version ?? undefined) : currentOf(l.file));
  const faded = (f: SpecFile) => !f.in_draft && "opacity-70";
  const revs = (v: FileVersion | undefined) => (
    <span className="flex min-w-0 gap-1">
      {(v ? (ix.revsByVersion.get(v.id) ?? []) : []).map((r) => (
        <Badge key={r} tone="ok">{`Rev ${r}`}</Badge>
      ))}
    </span>
  );
  const by = (v: FileVersion | undefined) => (v ? (v.uploaded_by ? (ix.userName.get(v.uploaded_by) ?? "a former user") : "HHL Ops") : "");

  const nameCell = (f: SpecFile, count: number) =>
    renaming === f.id ? (
      <span data-file={f.id} className="flex min-w-0 flex-1">
        <Input
          autoFocus
          defaultValue={f.name}
          className="h-6"
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            onRename(null);
            const name = e.target.value.trim();
            if (name && name !== f.name) mutate("spec_file.update", { id: f.id, patch: { name } });
          }}
          onKeyDown={(e) => {
            e.stopPropagation(); // the table's keys (j/k, Space) are the box's while renaming
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") onRename(null);
          }}
        />
      </span>
    ) : (
      <span
        data-file={f.id}
        className={cx("flex min-w-0 items-center gap-1.5", faded(f), overFile === f.id && "rounded bg-accent-soft px-1 text-accent-text")}
        title={overFile === f.id ? `Drop for a new version of ${f.name}` : undefined}
      >
        <span className="truncate font-medium">{f.name}</span>
        {count > 1 && (
          // A small count, so a narrow File column still shows the name (its arrow opens them)
          <span
            className="shrink-0 rounded bg-surface-2 px-1 text-[11px] text-faint tabular-nums"
            title={`${count} versions: open it for each one, with its notes and the revisions it was released in`}
          >
            {count}
          </span>
        )}
        {!f.in_draft && <Badge>left out</Badge>}
      </span>
    );
  const uploadCell = (f: SpecFile) => {
    const up = uploads.get(f.id);
    const current = currentOf(f);
    if (up) {
      const pct = (up.loaded / Math.max(1, up.total)) * 100;
      return (
        <span className="flex min-w-0 flex-1 items-center gap-2" title={`Uploading ${up.name}: ${bytes(up.loaded)} of ${bytes(up.total)}`}>
          <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded bg-surface-2">
            <span className="block h-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
          </span>
          <span className="truncate text-[11px] text-muted">
            Uploading {up.name} · {Math.floor(pct)}%
          </span>
        </span>
      );
    }
    return current ? (
      <span className={cx("truncate font-mono text-[12px] text-muted", faded(f))}>{current.filename}</span>
    ) : (
      <span className="truncate text-[12px] text-warn">Nothing uploaded yet</span>
    );
  };
  const draftBox = (f: SpecFile) => (
    <CellControls className="flex items-center">
      <input
        type="checkbox"
        aria-label="In next release"
        title="Released with the next revision. A file left out stays in the revisions that already have it."
        checked={f.in_draft}
        disabled={!editable}
        onChange={(e) => mutate("spec_file.update", { id: f.id, patch: { in_draft: e.target.checked } })}
        className="accent-[var(--accent)]"
      />
    </CellControls>
  );
  const fileActions = (f: SpecFile) => {
    const versions = ix.versionsByFile.get(f.id) ?? [];
    const current = currentOf(f);
    const released = versions.some((v) => ix.revsByVersion.has(v.id));
    return (
      <CellControls className="flex items-center">
        {current ? <VersionLinks version={current} /> : <span className="w-[44px]" />}
        {editable && (
          <>
            <RowAction title="Upload a new version (or drop it on this row)" onClick={() => onUpload(f)}>
              <UploadIcon className="size-3.5" />
            </RowAction>
            <RowAction title="Rename" onClick={() => onRename(f.id)}>
              <Pencil className="size-3.5" />
            </RowAction>
            <RowAction
              title={released ? "Released files can't be deleted: untick “In next release” to leave it out" : "Delete this file and its uploads"}
              danger
              disabled={released}
              onClick={() => confirm(`Delete “${f.name}” and its ${plural(versions.length, "upload")}?`) && mutate("spec_file.remove", { id: f.id })}
            >
              <Trash2 className="size-3.5" />
            </RowAction>
          </>
        )}
      </CellControls>
    );
  };
  const setKind = (f: SpecFile, kind: string) => mutate("spec_file.update", { id: f.id, patch: { kind: kind as SpecFile["kind"] } });

  const columns: Column<FileLine>[] = [
    {
      key: "name",
      header: "File",
      width: "minmax(160px,1.2fr)",
      value: (l) => l.file.name,
      groupCell: (group) => nameCell(fileOf(group), group.length),
      cell: (l, { nested }) =>
        nested ? (
          <span data-file={l.file.id} className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[12px] text-muted">Version {l.n}</span>
            {l.version?.id === l.file.current_version_id && <Badge tone="accent">current</Badge>}
          </span>
        ) : (
          nameCell(l.file, l.versions)
        ),
    },
    {
      key: "released",
      header: "Released",
      headerTitle: "The revisions it was released in",
      width: "110px",
      value: (l) => (l.version ? (ix.revsByVersion.get(l.version.id) ?? []).map((r) => `Rev ${r}`).join(", ") : ""),
      groupCell: (group) => revs(currentOf(fileOf(group))),
      cell: (l, { nested }) => revs(shownVersion(l, nested)),
    },
    {
      key: "filename",
      header: "Filename",
      width: "minmax(160px,1.5fr)",
      value: (l) => l.version?.filename ?? "",
      groupCell: (group) => uploadCell(fileOf(group)),
      cell: (l, { nested }) => (nested ? <span className="truncate font-mono text-[12px]">{l.version?.filename}</span> : uploadCell(l.file)),
    },
    {
      key: "size",
      header: "Size",
      width: "80px",
      align: "right",
      value: (l) => l.version?.size ?? null,
      groupCell: (group) => <span className="text-[12px] text-muted">{bytes(currentOf(fileOf(group))?.size ?? 0)}</span>,
      cell: (l, { nested }) => {
        const v = shownVersion(l, nested);
        return v ? <span className="text-[12px] text-muted">{bytes(v.size)}</span> : null;
      },
    },
    {
      key: "uploaded",
      header: "Uploaded",
      width: "110px",
      value: (l) => l.version?.uploaded_at ?? "",
      groupCell: (group) => {
        const v = currentOf(fileOf(group));
        return v ? <span className="truncate text-[12px] text-muted">{ago(v.uploaded_at)}</span> : null;
      },
      cell: (l, { nested }) => {
        const v = shownVersion(l, nested);
        return v ? <span className="truncate text-[12px] text-muted">{nested ? day(v.uploaded_at) : ago(v.uploaded_at)}</span> : null;
      },
    },
    {
      key: "note",
      header: "Note",
      width: "minmax(120px,1fr)",
      value: (l) => l.version?.note ?? "",
      groupCell: (group) => {
        const v = currentOf(fileOf(group));
        return (
          <span className="truncate text-[12px]" title={v?.note || undefined}>
            {v?.note}
          </span>
        );
      },
      cell: (l, { nested }) => {
        const v = shownVersion(l, nested);
        return (
          <span className="truncate text-[12px]" title={v?.note || undefined}>
            {v?.note}
          </span>
        );
      },
    },
    {
      key: "by",
      header: "By",
      width: "120px",
      optional: true,
      value: (l) => by(l.version ?? undefined),
      groupCell: (group) => <span className="truncate text-[12px] text-muted">{by(currentOf(fileOf(group)))}</span>,
      cell: (l, { nested }) => <span className="truncate text-[12px] text-muted">{by(shownVersion(l, nested))}</span>,
    },
    {
      key: "sha256",
      header: "SHA-256",
      width: "100px",
      optional: true,
      value: (l) => l.version?.sha256 ?? "",
      groupCell: (group) => {
        const v = currentOf(fileOf(group));
        return v ? (
          <span className="font-mono text-[12px] text-muted" title={`SHA-256 ${v.sha256}`}>
            {v.sha256.slice(0, 8)}
          </span>
        ) : null;
      },
      cell: (l, { nested }) => {
        const v = shownVersion(l, nested);
        return v ? (
          <span className="font-mono text-[12px] text-muted" title={`SHA-256 ${v.sha256}`}>
            {v.sha256.slice(0, 8)}
          </span>
        ) : null;
      },
    },
    {
      key: "kind",
      header: "Kind",
      headerTitle: "Kind of file",
      width: wide ? "200px" : "160px",
      value: (l) => SPEC_FILE_KIND_LABELS[l.file.kind],
      groupCell: (group) => <span className={cx("truncate", faded(fileOf(group)))}>{SPEC_FILE_KIND_LABELS[fileOf(group).kind]}</span>,
      cell: (l, { nested }) => (nested ? null : <span className={cx("truncate", faded(l.file))}>{SPEC_FILE_KIND_LABELS[l.file.kind]}</span>),
      // A file's row (not a version's) changes its kind; the header over its versions too.
      edit: { options: KIND_OPTIONS, can: (l) => l.versions <= 1, get: (l) => l.file.kind, save: (l, kind) => setKind(l.file, kind) },
      groupEdit: { options: KIND_OPTIONS, get: (group) => fileOf(group).kind, save: (group, kind) => setKind(fileOf(group), kind) },
    },
    {
      key: "in_draft",
      header: "In next release",
      headerTitle: "Released with the next revision. A file left out stays in the revisions that already have it.",
      width: "120px",
      value: (l) => l.file.in_draft,
      groupCell: (group) => draftBox(fileOf(group)),
      cell: (l, { nested }) => (nested ? null : draftBox(l.file)),
    },
    {
      key: "actions",
      header: "",
      width: "140px",
      value: () => null,
      groupCell: (group) => fileActions(fileOf(group)),
      cell: (l, { nested }) =>
        nested
          ? l.version && (
              <CellControls className="flex items-center">
                <VersionLinks version={l.version} />
              </CellControls>
            )
          : fileActions(l.file),
    },
    // The files in their own order, each one's versions newest first (never shown: what it's sorted by until someone
    // sorts it another way).
    { key: "order", header: "", optional: true, width: "0px", value: (l) => l.order },
  ];
  return (
    <DataTable
      fit={16}
      rows={rows}
      columns={columns}
      groupBy={(l) => l.file.id}
      groupNoun={["file", "files"]}
      rowNoun={["version", "versions"]}
      editing={editable}
      defaultSort={{ key: "order" }}
      exportName="Spec files"
      pageFilters={false}
    />
  );
}

/** "PCBA files: Gerbers ✓ · BOM + placement missing", from what the item needs (partNeeds: part type, packaging). */
function Needs({ item, files }: { item: NeedsItem; files: SpecFile[] }) {
  const needs = fileNeedStates(item, kindsWithUploads(files));
  if (!needs.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 px-1 pb-1 text-[12px]">
      <span className="text-muted">{needsLabel(item)} files:</span>
      {needs.map((n, k) => (
        <span key={n.label} className="inline-flex items-center gap-1">
          {k > 0 && <span className="text-faint">·</span>}
          {n.label}
          {n.have ? (
            <Check className="size-3.5 text-ok" aria-label="done" />
          ) : (
            <span className={n.optional ? "text-faint" : "text-warn"}>{n.optional ? "(optional)" : "missing"}</span>
          )}
        </span>
      ))}
    </div>
  );
}

/** What people chose for a group: its action ("new", "skip" or a slot's id) and kind. */
type Choice = { action?: string; kind?: SpecFileKind };

/**
 * The drop plan: one row per upload (what it is, its kind, new file or new version of which slot), Upload (Enter)
 * and Cancel (Esc). Upload zips and unzips in the browser, makes the new slots, then streams each file. For Esc it's
 * a dialog (`data-dialog`): the side pane leaves Esc to it while it's open.
 */
function DropPlan({
  spec,
  files,
  ix,
  drop,
  title,
  reading,
  uploading,
  onClose,
}: {
  spec: Spec;
  files: SpecFile[];
  ix: SpecIndex;
  drop: Drop;
  title: string;
  reading: boolean;
  /** Uploads under way, by slot. */
  uploading: ReadonlyMap<string, Upload>;
  /** `sent`: the plan was uploaded, made of the first `sent` dropped files. */
  onClose: (sent?: number) => void;
}) {
  const [keepWhole, setKeepWhole] = useState<ReadonlySet<number>>(new Set());
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  // The split toggle just used: its row is a new row after it (another group), so the focus goes back to it.
  const toggled = useRef<number | null>(null);
  // Upload has the focus (Enter), again once more dropped files have been read; Cancel when there's nothing to upload.
  useEffect(() => {
    if (reading) return;
    const submit = form.current?.querySelector<HTMLButtonElement>("button[type=submit]");
    (submit && !submit.disabled ? submit : form.current?.querySelector<HTMLButtonElement>("button[data-cancel]"))?.focus();
  }, [reading]);
  useEffect(() => {
    if (toggled.current === null) return;
    form.current?.querySelector<HTMLButtonElement>(`button[data-split="${toggled.current}"]`)?.focus();
    toggled.current = null;
  });
  // Esc cancels from anywhere in the plan, or when the focus is nowhere (the row it was on went away).
  const live = useRef({ busy, onClose });
  live.current = { busy, onClose };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as Node | null;
      if (e.key !== "Escape" || e.defaultPrevented || live.current.busy || (t !== document.body && !form.current?.contains(t))) return;
      e.preventDefault();
      live.current.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const slots = useMemo(
    () =>
      files.map((f) => ({
        id: f.id,
        name: f.name,
        kind: f.kind,
        filename: f.current_version_id ? ix.versionById.get(f.current_version_id)?.filename : null,
      })),
    [files, ix],
  );
  const kicad = !!me()?.kicad; // KiCad projects ---
  const groups = useMemo(() => planDrop(forPlan(drop.files), slots, { title, keepWhole, target: drop.target, kicad }), [drop, slots, title, keepWhole, kicad]);
  const actionOptions = useMemo(
    () => [
      { value: "new", label: "New file" },
      ...files.map((f) => ({ value: f.id, label: `New version of ${f.name}` })),
      { value: "skip", label: "Leave out" },
    ],
    [files],
  );

  const actionOf = (g: DropGroup) => {
    const a = choices[g.key]?.action ?? (g.leaveOut ? "skip" : (g.slot ?? "new"));
    return a === "new" || a === "skip" || files.some((f) => f.id === a) ? a : "new";
  };
  const kindOf = (g: DropGroup): SpecFileKind => choices[g.key]?.kind ?? files.find((f) => f.id === actionOf(g))?.kind ?? g.kind;
  const choose = (g: DropGroup, c: Choice) => setChoices((all) => ({ ...all, [g.key]: { ...all[g.key], ...c } }));
  const going = groups.filter((g) => actionOf(g) !== "skip");
  const versions = going.some((g) => actionOf(g) !== "new");

  const upload = async () => {
    if (busy || reading || !going.length) return;
    const sent = drop.files.length;
    setBusy(true);
    let made: File[];
    try {
      made = await makeUploads(going, drop.files);
    } catch (e) {
      toast.error((e as Error).message);
      setBusy(false);
      return;
    }
    let position = Math.max(-1, ...files.map((f) => f.position)) + 1;
    for (const [k, g] of going.entries()) {
      const action = actionOf(g);
      const kind = kindOf(g);
      if (action === "new") {
        const id = crypto.randomUUID();
        const ok = await mutate("spec_file.create", { id, spec_id: spec.id, name: g.name ?? fileSlotName(g.filename), kind, position: position++ });
        // A new file whose first upload fails is taken out again, so no empty slot is left behind (drop it again to retry).
        if (ok) send(id, made[k], "", g.kicad).then((went) => went || mutate("spec_file.remove", { id }));
      } else {
        if (files.find((f) => f.id === action)?.kind !== kind) mutate("spec_file.update", { id: action, patch: { kind } });
        send(action, made[k], note.trim(), g.kicad);
      }
    }
    onClose(sent);
  };
  const skipped = useMemo(() => skippedFiles(forPlan(drop.files)), [drop]);
  // A file dropped twice counts once.
  const dropped = new Set(drop.files.map((d) => d.path ?? d.file.name)).size;

  return (
    <form
      ref={form}
      data-dialog
      className="@container mb-1 rounded-md border border-accent bg-surface shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        upload();
      }}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 border-b border-line px-3 py-2">
        <span className="font-medium">
          {plural(dropped, "file")} → {plural(going.length, "upload")}
        </span>
        {skipped.length > 0 && (
          <span
            className="text-[12px] text-muted"
            title={[...skipped.slice(0, 30), ...(skipped.length > 30 ? [`and ${skipped.length - 30} more`] : [])].join("\n")}
          >
            · {skipped.length} skipped (system, backup and cache files, KiCad's leftovers)
          </span>
        )}
        <span className="text-[12px] text-muted">Sorted by their names: check each row, then upload</span>
      </div>
      <ul className="divide-y divide-line">
        {!groups.length && <li className="px-3 py-2 text-muted">Nothing to upload: only system files (like .DS_Store) were dropped.</li>}
        {groups.map((g) => {
          const action = actionOf(g);
          const zipName = g.splits ? drop.files[g.splits.file].file.name : "";
          const members = g.parts.map((p) => p.name).join("\n");
          const size = g.parts.length === 1 && !g.parts[0].entry ? drop.files[g.parts[0].file].file.size : 0;
          return (
            <li key={g.key} className="flex flex-col gap-1.5 px-3 py-2 @lg:flex-row @lg:items-center @lg:gap-2">
              <div className={cx("min-w-0 flex-1", action === "skip" && "opacity-60")}>
                <div className="flex min-w-0 items-center gap-1.5" title={members}>
                  {g.zip || /\.zip$/i.test(g.filename) ? (
                    <FileArchive className="size-3.5 shrink-0 text-muted" />
                  ) : (
                    <FileIcon className="size-3.5 shrink-0 text-muted" />
                  )}
                  <span className={cx("truncate font-medium", action === "skip" && "line-through")}>{g.filename}</span>
                </div>
                <div className="truncate text-[12px] text-muted" title={members}>
                  {[g.what, g.zip && `zipped from ${plural(g.parts.length, "file")}`, size > 0 && bytes(size)].filter(Boolean).join(" · ")}
                </div>
                {g.warning && (
                  <div className="flex items-center gap-1 text-[12px] text-warn">
                    <AlertTriangle className="size-3.5 shrink-0" /> {g.warning}
                  </div>
                )}
                {g.leaveOut && <div className="text-[12px] text-muted">{g.leaveOut}</div>}
                {action !== "new" && action !== "skip" && (uploading.has(action) || sending.has(action)) && (
                  <div className="text-[12px] text-muted">Goes after the upload in progress</div>
                )}
                {g.splits && (
                  <button
                    type="button"
                    data-split={g.splits.file}
                    disabled={busy}
                    className="text-[12px] text-accent-text hover:underline"
                    onClick={() => {
                      const file = g.splits?.file ?? -1;
                      toggled.current = file;
                      setKeepWhole((s) => (s.has(file) ? new Set([...s].filter((i) => i !== file)) : new Set([...s, file])));
                    }}
                  >
                    {g.splits.split
                      ? `Upload ${zipName} whole instead`
                      : g.splits.boards
                        ? "Split it into a Gerbers zip per board"
                        : "Split out its BOM and placement files"}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 @lg:flex @lg:shrink-0">
                <Select
                  aria-label="Kind"
                  value={kindOf(g)}
                  options={KIND_OPTIONS}
                  disabled={busy || action === "skip"}
                  onChange={(e) => choose(g, { kind: e.target.value as SpecFileKind })}
                  className="h-7 text-[12px] @lg:w-40"
                />
                <Select
                  aria-label="Where it goes"
                  value={action}
                  options={actionOptions}
                  disabled={busy}
                  onChange={(e) => choose(g, { action: e.target.value })}
                  className={cx("h-7 text-[12px] @lg:w-52", action !== "new" && action !== "skip" && "text-accent-text")}
                />
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-2">
        {versions && (
          <Input
            value={note}
            disabled={busy}
            placeholder="What changed? (optional, kept with the new versions)"
            onChange={(e) => setNote(e.target.value)}
            className="h-7 min-w-0 flex-1 basis-56 text-[12px]"
          />
        )}
        <div className="ml-auto flex items-center gap-2">
          {(busy || reading) && <LoaderCircle className="size-3.5 animate-spin text-muted" />}
          <Button size="sm" variant="ghost" kbd="Esc" disabled={busy} data-cancel onClick={() => onClose()}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" type="submit" kbd="↵" disabled={busy || reading || !going.length}>
            {busy ? "Preparing…" : going.length === 1 ? "Upload" : `Upload ${going.length}`}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** An icon button on a file's row; its title says what it does (and, disabled, why not). */
function RowAction({
  title,
  danger,
  disabled,
  onClick,
  children,
}: {
  title: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "rounded p-1 text-muted hover:bg-hover disabled:opacity-35 disabled:hover:bg-transparent",
        danger ? "hover:text-danger" : "hover:text-text",
      )}
    >
      {children}
    </button>
  );
}

/** View (PDFs, pictures, text; an empty slot otherwise, so rows line up) and Download. */
function VersionLinks({ version }: { version: FileVersion }) {
  return (
    <span className="flex shrink-0 items-center">
      {viewable(version.content_type) ? (
        <a href={downloadUrl(version.id, true)} target="_blank" rel="noreferrer" className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="View">
          <Eye className="size-3.5" />
        </a>
      ) : (
        <span className="w-[22px]" />
      )}
      <a
        href={downloadUrl(version.id)}
        download={version.filename}
        className="rounded p-1 text-muted hover:bg-hover hover:text-text"
        title={`Download ${version.filename}`}
      >
        <Download className="size-3.5" />
      </a>
    </span>
  );
}
